<template>
  <div id="app">
    <b-card>
      <b-card-header>
        <b-card-title>Monthly Spending by Platform</b-card-title>
      </b-card-header>
      <div v-if="formattedData.series.length > 0">
        <apexchart
          type="bar"
          :height="chartOptions.chart.height"
          :options="chartOptions"
          :series="formattedData.series"
        />
      </div>
      <div v-else class="text-center py-5">
        <i class="fas fa-chart-bar text-muted mb-3" style="font-size: 3rem;"></i>
        <h4 class="text-muted">No Spending Data Available</h4>
        <p>There is no marketing expense data for the selected date range. Try selecting a different period.</p>
      </div>
    </b-card>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import { BCard, BCardTitle, BCardHeader } from "bootstrap-vue";

export default {
  name: "MonthlySpendingByPlatform",
  components: {
    apexchart: VueApexCharts,
    BCard,
    BCardHeader,
    BCardTitle,
  },
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    formattedData() {
      if (!this.data || !Array.isArray(this.data) || this.data.length === 0) {
        return { series: [], categories: [] };
      }

      // Get unique years and months combinations
      const timePeriodsSet = new Set();
      this.data.forEach(item => {
        timePeriodsSet.add(`${item.year}-${item.month}`);
      });
      
      // Convert to array and sort chronologically
      const timePeriods = Array.from(timePeriodsSet)
        .sort((a, b) => {
          const [yearA, monthA] = a.split('-').map(Number);
          const [yearB, monthB] = b.split('-').map(Number);
          
          if (yearA !== yearB) return yearA - yearB;
          return monthA - monthB;
        });
      
      // Get formatted month names for display
      const categories = timePeriods.map(period => {
        const [year, month] = period.split('-').map(Number);
        const date = new Date(year, month - 1);
        return date.toLocaleString('default', { month: 'short' }) + ' ' + year;
      });
      
      // Get unique platforms
      const platforms = [...new Set(this.data.map(item => item.platform))];
      
      // Create series data
      const series = platforms.map(platform => {
        const platformData = {
          name: this.getPlatformFullName(platform),
          data: []
        };
        
        // For each time period, find the cost for this platform (or 0 if not found)
        timePeriods.forEach(period => {
          const [year, month] = period.split('-').map(Number);
          const expenseItem = this.data.find(item => 
            item.year === year && 
            item.month === month && 
            item.platform === platform
          );
          
          platformData.data.push(expenseItem ? expenseItem.totalCost : 0);
        });
        
        return platformData;
      });
      
      return { series, categories };
    },
    
    chartOptions() {
      return {
        chart: {
          height: 350,
          type: "bar",
          stacked: true,
          zoom: {
            enabled: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        colors: ["#1877F2", "#E4405F", "#1DA1F2", "#2867B2", "#FF0000", "#00A478"], // Facebook, Instagram, Twitter, LinkedIn, YouTube, TikTok colors
        grid: {
          row: {
            colors: ["#f3f3f3", "transparent"],
            opacity: 0.5,
          },
        },
        xaxis: {
          categories: this.formattedData.categories,
          title: {
            text: "Time Periods",
          },
        },
        yaxis: {
          title: {
            text: "Amount Spent ($)",
          },
        },
        tooltip: {
          theme: "dark",
          y: {
            formatter: function (val) {
              return `$${val}`;
            },
          },
        },
        legend: {
          position: "top",
        },
      };
    }
  },
  methods: {
    getPlatformFullName(shortName) {
      const platformNames = {
        'FB': 'Facebook',
        'IG': 'Instagram',
        'TW': 'Twitter',
        'LI': 'LinkedIn',
        'YT': 'YouTube',
        'TT': 'TikTok'
      };
      
      return platformNames[shortName] || shortName;
    }
  }
};
</script>

<style scoped>
#app {
  width: 100%;
  height: 100%;
}
</style>